import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getSecurityEventsTableColumns } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityEventsTable/securityEventsTableColumns';
import { SecurityEventFilterDto, SecurityEventRes } from '@dealstack/shared/lib/generated';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { useParams } from 'react-router-dom';
import { SecurityDetailsControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/useSecurityDetailsController.ts';

export const useSecurityEventsTableController = (hasEditorPermission?: boolean) => {
	const { securityId, personId } = useParams();
	const tCapManage = useTranslationCapManage();
	const securityController = useContext(SecurityDetailsControllerContext);
	const {
		getSecurityEventRequest,
		getSecurityDetailsRequest,
		securityEventListParams,
		setSecurityEventListParams,
	} = securityController;

	const [showCreateSecurityEventDrawer, setShowCreateSecurityEventDrawer] = useState(false);
	const [showEditSecurityEventDrawer, setShowEditSecurityEventDrawer] = useState(false);
	const [securityEventEdit, setSecurityEventEdit] = useState<SecurityEventRes>();
	const securityDetails = getSecurityDetailsRequest?.data;

	const eventColumns = useMemo(() => {
		if (!securityDetails) {
			return [];
		}

		return getSecurityEventsTableColumns(
			tCapManage,
			setShowEditSecurityEventDrawer,
			setSecurityEventEdit,
			securityDetails,
			getSecurityEventRequest.data,
			hasEditorPermission
		);
	}, [securityDetails, tCapManage, getSecurityEventRequest.data, hasEditorPermission]);

	const { mantineTableDataOperations, dataOperations } =
		useMantineTableWithPaginationFilteringSorting<SecurityEventRes, SecurityEventFilterDto>(
			eventColumns
		);

	const dataOperationsChanged = useMemo(() => {
		return (
			JSON.stringify({
				sortBy: dataOperations?.sortBy,
				filter: dataOperations?.filter,
				pagination: dataOperations?.pagination,
			}) !==
			JSON.stringify({
				sortBy: securityEventListParams?.sortBy,
				filter: securityEventListParams?.filter,
				pagination: securityEventListParams?.pagination,
			})
		);
	}, [securityEventListParams, dataOperations]);

	useEffect(() => {
		if (securityId && dataOperationsChanged) {
			setSecurityEventListParams({
				securityId,
				pagination: dataOperations?.pagination,
				filter: dataOperations?.filter,
				sortBy: dataOperations?.sortBy,
			});
		}
	}, [dataOperationsChanged]);

	const tableRef = useRef<HTMLDivElement | null>(null);

	const onSecurityCreateEventModalClose = async () => {
		setShowCreateSecurityEventDrawer(false);
	};

	const refetchEvents = () => {
		void getSecurityEventRequest.refetch();
	};

	return {
		tableRef,
		showCreateSecurityEventDrawer,
		setShowCreateSecurityEventDrawer,
		showEditSecurityEventDrawer,
		setShowEditSecurityEventDrawer,
		onSecurityCreateEventModalClose,
		getSecurityEventRequest,
		eventColumns,
		isTableLoading: getSecurityEventRequest?.isFetching,
		mantineTableDataOperations,
		securityEventEdit,
		setSecurityEventEdit,
		refetchEvents,
		securityId,
		personId,
	};
};
